import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  GraduationCap, Search, Users, Calendar, Mail, Phone, Building2,
  MapPin, Briefcase, Award, Edit, Eye, UserPlus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AlumniPage = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [alumni, setAlumni] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [editDialog, setEditDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  
  const [editForm, setEditForm] = useState({
    current_occupation: '',
    company: '',
    higher_education: '',
    current_city: '',
    contact_email: '',
    contact_phone: '',
    achievements: '',
    linkedin_url: ''
  });

  useEffect(() => {
    fetchAlumni();
  }, [yearFilter]);

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      // Get passout students from certificates API
      const res = await axios.get(`${API_URL}/api/certificates/students-by-status?status=passout`, { 
        headers: getAuthHeader() 
      });
      
      // Also get TC issued students as they might be alumni
      const tcRes = await axios.get(`${API_URL}/api/certificates/students-by-status?status=tc_issued`, { 
        headers: getAuthHeader() 
      });
      
      // Combine both lists
      const allAlumni = [...res.data, ...tcRes.data];
      
      // Filter by year if selected
      if (yearFilter !== 'all') {
        setAlumni(allAlumni.filter(a => a.passout_year === yearFilter || (a.leaving_date && a.leaving_date.startsWith(yearFilter))));
      } else {
        setAlumni(allAlumni);
      }
    } catch (error) {
      toast.error('Failed to fetch alumni data');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (alumniMember) => {
    setSelectedAlumni(alumniMember);
    setEditForm({
      current_occupation: alumniMember.current_occupation || '',
      company: alumniMember.company || '',
      higher_education: alumniMember.higher_education || '',
      current_city: alumniMember.current_city || '',
      contact_email: alumniMember.contact_email || alumniMember.email || '',
      contact_phone: alumniMember.contact_phone || alumniMember.phone || '',
      achievements: alumniMember.achievements || '',
      linkedin_url: alumniMember.linkedin_url || ''
    });
    setEditDialog(true);
  };

  const openViewDialog = (alumniMember) => {
    setSelectedAlumni(alumniMember);
    setViewDialog(true);
  };

  const saveAlumniDetails = async () => {
    if (!selectedAlumni) return;
    try {
      await axios.put(`${API_URL}/api/students/${selectedAlumni.id}`, editForm, {
        headers: getAuthHeader()
      });
      toast.success('Alumni details updated');
      setEditDialog(false);
      fetchAlumni();
    } catch (error) {
      toast.error('Failed to update alumni details');
    }
  };

  const filteredAlumni = alumni.filter(a => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      a.name?.toLowerCase().includes(query) ||
      a.student_id?.toLowerCase().includes(query) ||
      a.current_occupation?.toLowerCase().includes(query) ||
      a.company?.toLowerCase().includes(query)
    );
  });

  // Get unique years for filter
  const years = [...new Set(alumni.map(a => a.passout_year || (a.leaving_date ? a.leaving_date.slice(0, 4) : null)).filter(Boolean))].sort().reverse();

  const stats = [
    { title: 'Total Alumni', value: alumni.length, icon: Users, color: 'bg-blue-500' },
    { title: 'This Year', value: alumni.filter(a => a.passout_year === new Date().getFullYear().toString()).length, icon: GraduationCap, color: 'bg-green-500' },
    { title: 'With Contact', value: alumni.filter(a => a.contact_email || a.email).length, icon: Mail, color: 'bg-purple-500' },
  ];

  return (
    <DashboardLayout title="Alumni Management">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-xs font-medium text-muted-foreground">{stat.title}</CardTitle>
                    <div className={`${stat.color} p-2 rounded-lg`}><Icon className="h-3.5 w-3.5 text-white" /></div>
                  </CardHeader>
                  <CardContent><div className="text-2xl font-bold">{stat.value}</div></CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, ID, occupation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-alumni"
            />
          </div>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-48" data-testid="year-filter">
              <SelectValue placeholder="Filter by year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Alumni Grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading alumni...</div>
        ) : filteredAlumni.length === 0 ? (
          <Card className="p-12 text-center">
            <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No alumni found</p>
            <p className="text-sm text-muted-foreground mt-2">Students marked as "Passout" or "TC Issued" will appear here</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAlumni.map((member, index) => (
              <motion.div 
                key={member.id} 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: index * 0.03 }}
              >
                <Card className="hover:shadow-md transition-shadow" data-testid={`alumni-${member.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-lg font-semibold text-primary">
                            {member.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <CardTitle className="text-base">{member.name}</CardTitle>
                          <CardDescription className="font-mono text-xs">{member.student_id}</CardDescription>
                        </div>
                      </div>
                      <Badge variant={member.status === 'passout' ? 'default' : 'secondary'}>
                        {member.passout_year || (member.leaving_date ? member.leaving_date.slice(0, 4) : 'N/A')}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <GraduationCap className="h-4 w-4" />
                        <span>{member.final_class || member.class_name || 'Class N/A'}</span>
                      </div>
                      {member.current_occupation && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Briefcase className="h-4 w-4" />
                          <span>{member.current_occupation}</span>
                        </div>
                      )}
                      {member.company && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Building2 className="h-4 w-4" />
                          <span>{member.company}</span>
                        </div>
                      )}
                      {(member.contact_email || member.email) && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          <span className="truncate">{member.contact_email || member.email}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openViewDialog(member)}>
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                      <Button size="sm" variant="secondary" className="flex-1" onClick={() => openEditDialog(member)}>
                        <Edit className="h-4 w-4 mr-1" /> Update
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* View Alumni Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Alumni Profile</DialogTitle>
          </DialogHeader>
          {selectedAlumni && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-semibold text-primary">
                    {selectedAlumni.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedAlumni.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedAlumni.student_id}</p>
                  <Badge className="mt-1">{selectedAlumni.status === 'passout' ? 'Passout' : 'TC Issued'} - {selectedAlumni.passout_year || selectedAlumni.leaving_date?.slice(0, 4)}</Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Final Class</Label>
                  <p className="font-medium">{selectedAlumni.final_class || selectedAlumni.class_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Final Result</Label>
                  <p className="font-medium">{selectedAlumni.final_result || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Occupation</Label>
                  <p className="font-medium">{selectedAlumni.current_occupation || 'Not updated'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Company</Label>
                  <p className="font-medium">{selectedAlumni.company || 'Not updated'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Higher Education</Label>
                  <p className="font-medium">{selectedAlumni.higher_education || 'Not updated'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Current City</Label>
                  <p className="font-medium">{selectedAlumni.current_city || 'Not updated'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedAlumni.contact_email || selectedAlumni.email || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="font-medium">{selectedAlumni.contact_phone || selectedAlumni.phone || 'N/A'}</p>
                </div>
              </div>
              
              {selectedAlumni.achievements && (
                <div>
                  <Label className="text-muted-foreground">Achievements</Label>
                  <p className="text-sm mt-1">{selectedAlumni.achievements}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
            <Button onClick={() => { setViewDialog(false); openEditDialog(selectedAlumni); }}>
              <Edit className="h-4 w-4 mr-2" /> Edit Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Alumni Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Alumni Information</DialogTitle>
          </DialogHeader>
          {selectedAlumni && (
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedAlumni.name}</p>
                <p className="text-sm text-muted-foreground">{selectedAlumni.student_id}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Current Occupation</Label>
                  <Input
                    value={editForm.current_occupation}
                    onChange={e => setEditForm({...editForm, current_occupation: e.target.value})}
                    placeholder="e.g., Software Engineer"
                  />
                </div>
                <div>
                  <Label>Company/Organization</Label>
                  <Input
                    value={editForm.company}
                    onChange={e => setEditForm({...editForm, company: e.target.value})}
                    placeholder="e.g., Google"
                  />
                </div>
                <div>
                  <Label>Higher Education</Label>
                  <Input
                    value={editForm.higher_education}
                    onChange={e => setEditForm({...editForm, higher_education: e.target.value})}
                    placeholder="e.g., B.Tech from IIT"
                  />
                </div>
                <div>
                  <Label>Current City</Label>
                  <Input
                    value={editForm.current_city}
                    onChange={e => setEditForm({...editForm, current_city: e.target.value})}
                    placeholder="e.g., Bangalore"
                  />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={editForm.contact_email}
                    onChange={e => setEditForm({...editForm, contact_email: e.target.value})}
                    placeholder="alumni@email.com"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={editForm.contact_phone}
                    onChange={e => setEditForm({...editForm, contact_phone: e.target.value})}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
              
              <div>
                <Label>LinkedIn URL</Label>
                <Input
                  value={editForm.linkedin_url}
                  onChange={e => setEditForm({...editForm, linkedin_url: e.target.value})}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              
              <div>
                <Label>Achievements & Notes</Label>
                <Textarea
                  value={editForm.achievements}
                  onChange={e => setEditForm({...editForm, achievements: e.target.value})}
                  placeholder="Notable achievements, awards, positions held..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={saveAlumniDetails} data-testid="save-alumni-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AlumniPage;
